import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_WEBHOOK } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { object, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viArrowUpToLine,
  viLifeRing,
  viWebhooks,
  viWebhooksClock,
} from "@sippy-platform/valkyrie";

import {
  Alert,
  AlertTitle,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import webhookReducer from "./webhookReducer";

function Webhook() {
  const { id, pbxId } = useParams();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pbx = useSelector((state) => state.domain.pbx);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getPbx());
  }, []);

  const {
    value: webhook,
    dispatch: webhookDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_WEBHOOK,
    reducer: webhookReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 40 characters." }),
      url: string()
        .required({ message: "URL is required." })
        .length([1, 350], {
          message: "The URL must be at most 350 characters.",
        })
        .pattern(
          "^(http(|s):\\/\\/.)[-a-zA-Z0-9@:%._\\+~#=]{2,351}\\.[a-z]{2,14}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=\\{\\}]*)$",
          { message: "URL is not valid." },
        ),
      requestType: string().required({ message: "Request type is required." }),
      customHeader: object({
        name: string()
          .required({
            condition: !!webhook.customHeader.value,
            message: "Name of the custom header is required.",
          })
          .length([1, 40], {
            message: "Name of the custom header must be at most 40 characters.",
          })
          .pattern("^[a-zA-Z][a-zA-Z-]*[a-zA-Z]$", {
            message: "Name of the custom header is not valid.",
          }),
        value: string()
          .required({
            condition: !!webhook.customHeader.name,
            message: "Value of the custom header is required.",
          })
          .length([1, 255], {
            message: "Value of the custom header must be at most 255 characters.",
          })
          .pattern("^[a-zA-Z0-9_ :;.,\\\\/”‘?!(){}\\[\\]@<>=\\-+*#$&`|~^%]*$", {
            message: "Value of the custom header is not valid.",
          }),
      }),
    }),
    onSubmit: () => Api.storeWebhook(webhook),
    onSuccess: (response) => {
      if (!webhook.id) {
        dispatch({ type: "id", value: response.data.webhook.id });
        navigate(`../${response.data.webhook.id}`);
      }

      Promise.resolve(Api.getWebhooks());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === webhook.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      webhookDispatch({ type: "set", value: NEW_WEBHOOK });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getWebhook({ id: Number(id) }))
      .then((response) => !ignore && webhookDispatch({ type: "set", value: response.data.webhook }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [id, webhook.id, webhookDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    webhookDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  function migrateToModernWebhook() {
    webhookDispatch({ type: "migrateToModernWebhook" });
    setSaveState(SaveState.UNSAVED);
  }

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getWebhooks()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${webhook.name || "Webhook"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={webhook.name} icon={viWebhooks} />
              </IconButton>
            ) : (
              <Avatar text={webhook.name} icon={viWebhooks} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={webhook.id || "nk"}
                isActionable={!!webhook?.id}
                onDeleteCheck={() => Api.deleteWebhook(webhook, false)}
                onDelete={() => Api.deleteWebhook(webhook, true)}
                name={webhook.name}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={webhook.name || "Unnamed webhook"}
        />
        {!webhook.useNewWebhooks && (
          <Alert severity="warning" sx={{ boxShadow: 1 }}>
            <AlertTitle>This is a legacy webhook</AlertTitle>
            <Typography>
              Sippy will soon deprecate this type of webhook. You can migrate this webhook if you're ready to do so. You
              won't be able to revert this change.
            </Typography>

            <Stack
              direction="row"
              sx={{
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                variant="contained"
                color="warning"
                startIcon={<Valkyrie icon={viLifeRing} />}
                onClick={() => window.open(`${urls.docs}docs/webhooks/webhooks`, "_blank")}
              >
                Help
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<Valkyrie icon={viArrowUpToLine} />}
                onClick={migrateToModernWebhook}
              >
                Migrate now
              </Button>
            </Stack>
          </Alert>
        )}
        <FormGroup
          title="Identity"
          actions={
            !!webhook.id && (
              <Button
                variant="contained"
                component={NavLink}
                to={`/pbx/${pbxId}/logs/webhooks?webhook=${webhook.id}`}
                startIcon={<Valkyrie icon={viWebhooksClock} />}
              >
                View log
              </Button>
            )
          }
        >
          {webhook.useNewWebhooks && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputToggle
                id="enabled"
                error={errors}
                onChange={formHandler}
                states={[false, true]}
                value={webhook.enabled}
                primary="Enable this webhook"
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={webhook.name} error={errors} />
          </Grid>
          {!webhook.useNewWebhooks && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputToggle
                id="onAnswer"
                error={errors}
                onChange={formHandler}
                states={[0, 1]}
                value={webhook.onAnswer}
                primary="On answer"
                secondary="Call the webhook only when a call has been answered."
              />
            </Grid>
          )}
        </FormGroup>
        <FormGroup title="Request">
          <Grid size={12}>
            <InputText
              required
              id="url"
              label="URL"
              onChange={formHandler}
              value={webhook.url}
              error={errors}
              multiline
              rows={2}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Custom header">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              id="customHeader.name"
              name="customHeader.name"
              label="Name"
              onChange={formHandler}
              value={webhook.customHeader?.name}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              id="customHeader.value"
              name="customHeader.value"
              label="Value"
              onChange={formHandler}
              value={webhook.customHeader?.value}
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Response">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputSelect
              id="requestType"
              label="Request type"
              onChange={formHandler}
              value={webhook.requestType}
              getOptionLabel={(x) => x.name}
              selects={[
                { value: "json_body", name: "JSON" },
                { value: "querystring", name: "Query string" },
              ]}
              error={errors}
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Webhook;
